/**
 * Tradução de erro de servidor para frase de gente.
 *
 * Regra: a tela NUNCA mostra `[object Object]`, stack trace, nem "Erro 500".
 * Se não souber o que aconteceu, diz isso com todas as letras e oferece o que
 * fazer a seguir.
 *
 * Não depende de tRPC nem de Better Auth em tempo de execução: lê a forma do
 * objeto. Assim continua funcionando se um dos dois mudar o formato do erro.
 */

/** Erro vindo do cliente de autenticação (Better Auth devolve `{ error }`). */
export class AuthRequestError extends Error {
  constructor(
    message: string,
    readonly code: string | null,
    readonly status: number | null,
  ) {
    super(message);
    this.name = 'AuthRequestError';
  }
}

function readString(source: unknown, key: string): string | null {
  if (typeof source !== 'object' || source === null) return null;
  const value = (source as Record<string, unknown>)[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function readNumber(source: unknown, key: string): number | null {
  if (typeof source !== 'object' || source === null) return null;
  const value = (source as Record<string, unknown>)[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Status HTTP por trás do erro, venha ele de onde vier.
 * O `retry` do React Query depende disso para não insistir em 401/403.
 */
export function httpStatusFromError(error: unknown): number | null {
  if (error === null || typeof error !== 'object') return null;

  const direct = readNumber(error, 'status') ?? readNumber(error, 'statusCode');
  if (direct !== null) return direct;

  // tRPC: error.data.httpStatus
  const data = (error as { data?: unknown }).data;
  const fromData = readNumber(data, 'httpStatus') ?? readNumber(data, 'status');
  if (fromData !== null) return fromData;

  // erro embrulhado: { error: { status } }
  const nested = (error as { error?: unknown }).error;
  return readNumber(nested, 'status');
}

/** Código de erro simbólico (tRPC `data.code` ou Better Auth `error.code`). */
export function codeFromError(error: unknown): string | null {
  if (error === null || typeof error !== 'object') return null;
  if (error instanceof AuthRequestError) return error.code;

  const data = (error as { data?: unknown }).data;
  return (
    readString(error, 'code') ??
    readString(data, 'code') ??
    readString((error as { error?: unknown }).error, 'code')
  );
}

/** `true` quando a sessão acabou ou o papel não permite — nunca se repete. */
export function isAuthError(error: unknown): boolean {
  const status = httpStatusFromError(error);
  if (status === 401 || status === 403) return true;
  const code = codeFromError(error);
  return code === 'UNAUTHORIZED' || code === 'FORBIDDEN';
}

/**
 * Mensagens por código. A chave é o que o servidor manda; o valor é o que o
 * usuário lê. Tudo que não está aqui cai no fallback por status.
 */
const BY_CODE: Record<string, string> = {
  // --- Better Auth ---
  INVALID_EMAIL_OR_PASSWORD: 'E-mail ou senha incorretos.',
  INVALID_EMAIL: 'Esse e-mail não parece válido.',
  INVALID_PASSWORD: 'E-mail ou senha incorretos.',
  USER_ALREADY_EXISTS: 'Já existe uma conta com esse e-mail. Tente entrar.',
  USER_NOT_FOUND: 'Não encontramos uma conta com esse e-mail.',
  EMAIL_NOT_VERIFIED: 'Confirme seu e-mail antes de entrar. Reenviamos o link se precisar.',
  PASSWORD_TOO_SHORT: 'A senha precisa de pelo menos 8 caracteres.',
  INVALID_TOKEN: 'Esse link expirou ou já foi usado. Peça um novo.',
  TOKEN_EXPIRED: 'Esse link expirou. Peça um novo.',
  SESSION_EXPIRED: 'Sua sessão expirou. Entre de novo.',
  // --- domínio (packages/shared/src/errors) ---
  UNAUTHORIZED: 'Sua sessão expirou. Entre de novo.',
  FORBIDDEN: 'Seu papel neste workspace não permite esta ação.',
  NOT_FOUND: 'Não encontramos esse registro.',
  CONFLICT: 'Esse registro já existe.',
  VALIDATION: 'Confira os campos destacados.',
  RATE_LIMITED: 'Muitas tentativas seguidas. Espere um minuto e tente de novo.',
  TOO_MANY_REQUESTS: 'Muitas tentativas seguidas. Espere um minuto e tente de novo.',
  ENTITLEMENT_EXCEEDED: 'Seu plano chegou ao limite para esta ação.',
  PROVIDER_ERROR: 'Um serviço externo falhou. Tente de novo em instantes.',
  INTERNAL: 'Algo quebrou do nosso lado. Já registramos o erro.',
};

const BY_STATUS: Record<number, string> = {
  400: 'Confira os campos destacados.',
  401: 'Sua sessão expirou. Entre de novo.',
  403: 'Seu papel neste workspace não permite esta ação.',
  404: 'Não encontramos esse registro.',
  409: 'Esse registro já existe.',
  422: 'Confira os campos destacados.',
  429: 'Muitas tentativas seguidas. Espere um minuto e tente de novo.',
  500: 'Algo quebrou do nosso lado. Já registramos o erro.',
  502: 'Não conseguimos falar com o servidor. Tente de novo em instantes.',
  503: 'O servidor está indisponível no momento. Tente de novo em instantes.',
  504: 'O servidor demorou demais para responder.',
};

const FALLBACK = 'Não conseguimos concluir agora. Tente de novo em instantes.';

/** Mensagem exibível. Sempre devolve algo legível — nunca string vazia. */
export function humanizeError(error: unknown): string {
  if (error === null || error === undefined) return FALLBACK;
  if (typeof error === 'string' && error.length > 0 && error.length <= 200) return error;

  /*
   * A frase da REGRA DE NEGÓCIO vem antes da genérica do código (A5, 07/10). Antes isto só
   * valia no diálogo da Meta, que chamava `domainMessage()` à mão; nas outras ~30 telas um
   * "Esse grupo já está em outra campanha" do servidor virava "Esse registro já existe."
   */
  const dominio = domainMessage(error);
  if (dominio !== null) return dominio;

  // O código do DOMÍNIO é mais preciso que o de transporte ("PROVIDER_ERROR" × "BAD_GATEWAY").
  const appCode = readString((error as { data?: unknown }).data, 'appCode');
  if (appCode !== null && BY_CODE[appCode] !== undefined) return BY_CODE[appCode];

  const code = codeFromError(error);
  if (code !== null && BY_CODE[code] !== undefined) return BY_CODE[code];

  const status = httpStatusFromError(error);
  if (status !== null && BY_STATUS[status] !== undefined) return BY_STATUS[status];

  // Mensagem do servidor só vale se for frase; "Error: fetch failed" não é.
  const message =
    error instanceof Error
      ? error.message
      : (readString(error, 'message') ??
        readString((error as { error?: unknown }).error, 'message'));

  if (
    message !== null &&
    message.length > 0 &&
    message.length <= 200 &&
    !/^\w*Error:/.test(message)
  ) {
    return message;
  }

  if (message !== null && /fetch|network|failed to fetch/i.test(message)) {
    return 'Sem conexão com o servidor. Verifique sua internet.';
  }

  return FALLBACK;
}

/** Frase de regra de negócio maior que isto é despejo de dado, não frase. */
const MAX_FRASE_DE_DOMINIO = 400;

/**
 * A frase do servidor, quando o erro é uma REGRA DE NEGÓCIO (`AppError`, que chega com
 * `data.appCode`). Essas frases são escritas para gente e dizem a causa — "a Meta recusou
 * este token…" — e o `humanizeError` as trocaria pela genérica do código ("Confira os
 * campos destacados", sem campo destacado nenhum). `null` para o resto (erro técnico,
 * validação de formato do zod), que continua no caminho genérico.
 */
export function domainMessage(error: unknown): string | null {
  if (error === null || typeof error !== 'object') return null;
  const data = (error as { data?: { appCode?: unknown } | null }).data;
  if (data === null || data === undefined || typeof data.appCode !== 'string') return null;
  /*
   * `ProviderError` não é frase de gente: chega como "[waha] GET /x devolveu HTTP 500" — o
   * nome do fornecedor e a rota, que a tela não deve mostrar. Esse cai no genérico do código.
   */
  if (data.appCode === 'PROVIDER_ERROR') return null;
  const message = (error as { message?: unknown }).message;
  if (typeof message !== 'string') return null;
  const frase = message.trim();
  if (frase.length === 0 || frase.length > MAX_FRASE_DE_DOMINIO) return null;
  if (frase.startsWith('[')) return null;
  return frase;
}
